import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader } from "@cx/ui";
import type { ListResult, ProductListItem } from "@cx/contracts";
import { useSession } from "../../session";
import { StockPanel } from "./StockPanel";

const FORMAT: Record<string, string> = { S: "Simples", V: "Com variações", E: "Kit" };

/** Controle de estoque (RF-SUP-01): escolhe o produto e mostra saldos, extrato, lançamento e transferência. */
export function StockPage(): ReactElement {
  const { companyId, api } = useSession();
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<ProductListItem | null>(null);
  const term = q.trim();
  const found = useQuery({
    queryKey: ["products", companyId, { q: term, picker: "stock" }],
    enabled: term.length >= 2,
    queryFn: () =>
      api<ListResult<ProductListItem>>(
        `/products?${new URLSearchParams({ q: term, includeVariations: "yes", limit: "8" })}`,
      ),
  });
  return (
    <>
      <ContentHeader title="Controle de estoque" breadcrumb={["Suprimentos", "Controle de estoque"]} />
      <div className="box card p-3 mb-3">
        <label className="form-label" htmlFor="stock-product-search">
          Produto
        </label>
        <input
          id="stock-product-search"
          type="search"
          className="form-control"
          placeholder="Digite nome, SKU ou GTIN do produto"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {term.length >= 2 && (
          <ul className="list-group mt-1" aria-label="Produtos encontrados">
            {found.isLoading && <li className="list-group-item text-muted">Buscando…</li>}
            {found.isSuccess && found.data.data.length === 0 && (
              <li className="list-group-item text-muted">Nenhum produto encontrado.</li>
            )}
            {found.data?.data.map((p) => (
              <li key={p.id} className="list-group-item d-flex align-items-center gap-2">
                <span className="flex-grow-1">
                  {p.name}
                  {p.sku && <span className="text-muted small ms-2">{p.sku}</span>}
                  <span className="text-muted small ms-2">{FORMAT[p.format]}</span>
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  aria-label={`Ver estoque de ${p.name}`}
                  onClick={() => {
                    setPicked(p);
                    setQ("");
                  }}
                >
                  Ver estoque
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {picked && (
        <div className="box card p-3">
          <h2 className="h5">
            <Link to="/cadastros/produtos/$id" params={{ id: picked.id }}>
              {picked.name}
            </Link>
            {picked.sku && <span className="text-muted small ms-2">{picked.sku}</span>}
          </h2>
          <StockPanel key={picked.id} productId={picked.id} />
        </div>
      )}
    </>
  );
}
